import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  ChevronLeft,
  ChevronRight,
  Plus,
  RefreshCw,
  Loader2,
  Building2,
  Search,
  X,
  TrendingUp,
  Activity,
  CheckCircle2,
  Warehouse
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import { Progress } from '@/components/ui/progress';


interface Vendor {
  id: string;
  name: string;
  status: string;
  created_at?: string;
  updated_at?: string;
  created_by?: string;
  updated_by?: string;
}

interface VendorSummary {
  totalVendors: number;
  activeVendors: number;
  inactiveVendors: number;
}


const Vendors = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const { toast } = useToast();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [filteredVendors, setFilteredVendors] = useState<Vendor[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [summaryData, setSummaryData] = useState<VendorSummary>({
    totalVendors: 0,
    activeVendors: 0,
    inactiveVendors: 0
  });

  


  useEffect(() => {
    fetchVendors();
    fetchVendorsSummary();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === '' && statusFilter === 'all') {
      setFilteredVendors(vendors);
    } else {
      let filtered = [...vendors];
      
      // Apply text search filter
      if (searchTerm.trim()) {
        filtered = filtered.filter(vendor => 
          vendor.name?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      
      // Apply status filter
      if (statusFilter !== 'all') {
        filtered = filtered.filter(vendor => vendor.status === statusFilter);
      }
      
      setFilteredVendors(filtered);
    }
    setCurrentPage(1);
  }, [vendors, searchTerm, statusFilter]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredVendors.length / pageSize));
  }, [filteredVendors, pageSize]);

  const fetchVendors = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*')
        .order('name');

      if (error) throw error;
      
      setVendors(data || []);
      setFilteredVendors(data || []);
    } catch (error) {
      console.error('Erro ao buscar fornecedores:', error);
      toast({
        title: 'Erro',
        description: 'Não foi possível carregar os fornecedores.',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchVendorsSummary = async () => {
    try {
      const { count: totalCount, error: totalError } = await supabase
        .from('vendors')
        .select('*', { count: 'exact', head: true });
      if (totalError) throw totalError;

      const { count: activeCount, error: activeError } = await supabase
        .from('vendors')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'active');
      if (activeError) throw activeError;

      const { count: inactiveCount, error: inactiveError } = await supabase
        .from('vendors')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'inactive');
      if (inactiveError) throw inactiveError;

      setSummaryData({
        totalVendors: totalCount || 0,
        activeVendors: activeCount || 0,
        inactiveVendors: inactiveCount || 0,
      });

    } catch(error) {
      console.error("Error fetching vendor summary:", error);
    }
  }



  const getInitials = (name: string): string => {
    if (!name) return '?';
    return name
      .split(' ')
      .map(part => part.charAt(0))
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  const handleRowClick = (vendorId: string) => {
    navigate(`/vendors/${vendorId}`);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  // Function to navigate to the next page
  const nextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Function to navigate to the previous page
  const previousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  // Function to change page size
  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value));
    setCurrentPage(1); // Reset to first page
  };

  // Function to get current page items
  const getCurrentPageItems = () => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredVendors.slice(startIndex, endIndex);
  };



  const handleCreateVendor = () => {
    navigate('/vendors/new');
  };

  return (
    <>
    <Helmet>
      <title>CSM | Trivium - Fornecedores</title>
    </Helmet>
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100">
      {/* Modern Header with Gradient Background */}
      <div className="relative overflow-hidden bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-700">
        <div className="absolute inset-0 bg-black/20" />
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5" />
        
        <div className="relative container mx-auto px-6 py-6 pb-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
            <div className="space-y-2">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-lg">
                  <Warehouse className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-white">Fornecedores</h1>
                  <p className="text-blue-100 text-base pb-2">
                    Gerencie seus fornecedores e parceiros de negócio
                  </p>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button 
                size="default" 
                onClick={handleCreateVendor} 
                className="bg-white text-gray-900 hover:bg-gray-100 shadow-lg"
              >
                <Plus className="h-4 w-4 mr-2" />
                Novo Fornecedor
              </Button>
            </div>
          </div>
        </div>
      </div>

        <div className="container mx-auto px-6 -mt-4 relative z-10">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-blue-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-100 rounded-md">
                      <Building2 className="h-4 w-4 text-blue-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Total de Fornecedores</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.totalVendors}
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="text-xs text-gray-500">
                    Parceiros cadastrados
                  </div>
                  <div className="flex items-center gap-1">
                    <TrendingUp className="h-3 w-3 text-blue-500" />
                    <span className="text-xs text-green-600 font-extrabold">+5%</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-green-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-green-100 rounded-md">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Fornecedores Ativos</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.activeVendors}
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={summaryData.totalVendors > 0 ? (summaryData.activeVendors / summaryData.totalVendors) * 100 : 0} 
                    className="h-1.5"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {summaryData.totalVendors > 0 
                        ? `${Math.round((summaryData.activeVendors / summaryData.totalVendors) * 100)}% do total` 
                        : 'Nenhum fornecedor'}
                    </div>
                    <div className="flex items-center gap-1">
                      <Activity className="h-3 w-3 text-green-500" />
                      <span className="text-xs text-green-600 font-extrabold">Online</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="relative overflow-hidden border-0 shadow-lg bg-gradient-to-br from-red-50 to-white">
              <div className="absolute top-0 right-0 w-20 h-20 bg-gradient-to-br from-red-500/10 to-transparent rounded-full transform translate-x-10 -translate-y-10" />
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-red-100 rounded-md">
                      <X className="h-4 w-4 text-red-600" />
                    </div>
                    <span className="text-sm font-extrabold text-gray-600">Fornecedores Inativos</span>
                  </div>
                  <div className="text-2xl font-extrabold text-gray-900">
                    {summaryData.inactiveVendors}
                  </div>
                </div>
                <div className="space-y-2">
                  <Progress 
                    value={summaryData.totalVendors > 0 ? (summaryData.inactiveVendors / summaryData.totalVendors) * 100 : 0} 
                    className="h-1.5"
                  />
                  <div className="flex items-center justify-between">
                    <div className="text-xs text-gray-500">
                      {summaryData.totalVendors > 0 
                        ? `${Math.round((summaryData.inactiveVendors / summaryData.totalVendors) * 100)}% do total` 
                        : 'Nenhum fornecedor'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="border-0 shadow-xl bg-white/80 backdrop-blur-sm">
            <CardContent className="p-6">
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center mb-6">
                <div className="relative w-full sm:w-auto flex-1">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Buscar fornecedores..."
                    className="pl-8 h-10 bg-white border-gray-200 focus:border-blue-500 focus:ring-blue-500"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <button
                      className="absolute right-2.5 top-2.5"
                      onClick={clearSearch}
                      type="button"
                    >
                      <X className="h-4 w-4 text-muted-foreground" />
                    </button>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Select 
                    value={statusFilter} 
                    onValueChange={setStatusFilter}
                  >
                    <SelectTrigger className="w-full sm:w-32 h-10 bg-white border-gray-200">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      <SelectItem value="active">Ativos</SelectItem>
                      <SelectItem value="inactive">Inativos</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {loading ? (
                <div className="py-8">
                  <div className="flex justify-center">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                  </div>
                  <p className="text-center mt-2 text-muted-foreground">Carregando fornecedores...</p>
                </div>
              ) : filteredVendors.length === 0 ? (
                <div className="py-16 text-center">
                  <Building2 className="h-12 w-12 mx-auto text-muted-foreground" />
                  <h3 className="mt-4 text-lg font-extrabold">Nenhum fornecedor encontrado</h3>
                  <p className="mt-1 text-muted-foreground">
                    {searchTerm || statusFilter !== 'all'
                      ? 'Tente ajustar os filtros de busca'
                      : 'Comece adicionando seu primeiro fornecedor'}
                  </p>
                </div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fornecedor</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Criado em</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {getCurrentPageItems().map((vendor) => (
                        <TableRow 
                          key={vendor.id} 
                          onClick={() => handleRowClick(vendor.id)}
                          className="cursor-pointer hover:bg-muted/50"
                        >
                          <TableCell className="font-extrabold">
                            <div className="flex items-center gap-3">
                              <div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center text-primary font-extrabold">
                                {getInitials(vendor.name)}
                              </div>
                              {vendor.name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center">
                              <span className={`h-2 w-2 rounded-full mr-2 ${
                                vendor.status === 'active' ? 'bg-green-500' : 'bg-red-500'
                              }`} />
                              {vendor.status === 'active' ? 'Ativo' : 'Inativo'}
                            </div>
                          </TableCell>
                          <TableCell>
                            {vendor.created_at 
                              ? new Date(vendor.created_at).toLocaleDateString('pt-BR')
                              : <span className="text-muted-foreground">-</span>
                            }
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  <div className="flex flex-col sm:flex-row items-center justify-between p-4 border-t">
                    <div className="flex items-center space-x-2 mb-4 sm:mb-0">
                      <Select value={pageSize.toString()} onValueChange={handlePageSizeChange}>
                        <SelectTrigger className="w-20">
                          <SelectValue placeholder={pageSize.toString()} />
                        </SelectTrigger>
                        <SelectContent side="top">
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="20">20</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        Exibindo <span className="font-extrabold">{Math.min((currentPage - 1) * pageSize + 1, filteredVendors.length)}</span> até{" "}
                        <span className="font-extrabold">{Math.min(currentPage * pageSize, filteredVendors.length)}</span> de{" "}
                        <span className="font-extrabold">{filteredVendors.length}</span> resultados
                      </p>
                    </div>
                    <div className="flex items-center space-x-2">
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={previousPage}
                          disabled={currentPage === 1}
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                      <div className="text-sm">
                        Página <span className="font-extrabold">{currentPage}</span> de{" "}
                        <span className="font-extrabold">{totalPages}</span>
                      </div>
                        <Button
                          variant="outline"
                          size="icon"
                          onClick={nextPage}
                          disabled={currentPage === totalPages}
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
          <div className="h-16" />
        </div>
      </div>
    </>
  );
};

export default Vendors; 